import {Skin} from "@pixi-spine/runtime-3.8";
import {selectRandom, spine} from "p3";
import definitions from "../../../definitions";
import Player from "./Player";

export default class PlayerIvandoe extends Player {
    constructor() {
        super();

        this._idleAnimations = [
            {body:"body_idle_00", legs:"legs_idle_00"},
            {body:"body_idle_01", legs:"legs_idle_01"},
            {body:"body_idle_02", legs:"legs_idle_02"},
            {legs:"legs_idle_00"}
        ];

        this._walkAnimations = [
            {body:"body_walk_u_00", legs:"legs_walk_u_00"},
            {body:"body_walk_d_00", legs:"legs_walk_d_00"},
            {body:"body_walk_r_00", legs:"legs_walk_r_00"},

            {body:"body_walk_u_sword_00", legs:"legs_walk_u_00"},
            {body:"body_walk_d_sword_00", legs:"legs_walk_d_00"},
            {body:"body_walk_r_sword_00", legs:"legs_walk_r_00"}
        ];

        this._attackAnimations = [
            {body:"body_attack_r_sword_00", legs:"legs_attack_r_sword_00"},
            {body:"body_attack_r_sword_01", legs:"legs_attack_r_sword_00"}
        ];

        this._takeDamageAnimations = [
            {body:"body_hurt_r_00", legs:"legs_hurt_r_00"},
            {body:"body_hurt_r_sword_00", legs:"legs_hurt_r_00"}
        ];

        this._talkAnimations = [
            {body:"body_quest_talk_00"}
        ]

        this._eatAnimations = [
            {body:"body_eat_r_00"},
            {body:"body_eat_r_sword_01"}
        ];

        this._grabAnimations = [
            {body:"body_grab_r_00"},
            {body:"body_grab_r_sword_00"}
        ];

        this._questAnimations = [
            {body:"body_quest_start_00"},
            {body:"body_quest_complete_00"}
        ]

        this._holdingAnimations = [
            {body:"body_walk_r_chicken_00"}
        ]

        this._deathAnimations = [
            {body:"die_r_00"}
        ];

        this._animationNames = {
            walk_u: [this._walkAnimations[0]],
            walk_d: [this._walkAnimations[1]],
            walk_r: [this._walkAnimations[2]],
            walk_u_sword: [this._walkAnimations[3]],
            walk_d_sword: [this._walkAnimations[4]],
            walk_r_sword: [this._walkAnimations[5]],
            walk_legs_u: [this._walkAnimations[6]],
            walk_legs_d: [this._walkAnimations[7]],
            walk_legs_r: [this._walkAnimations[8]],
            hurt: [this._takeDamageAnimations[0]],
            hurt_sword: [this._takeDamageAnimations[1]],
            grab: [this._grabAnimations[0]],
            grab_sword: [this._grabAnimations[1]],
            eat: [this._eatAnimations[0]],
            eat_sword: [this._eatAnimations[1]],
            quest_start: [this._questAnimations[0]],
            quest_complete: [this._questAnimations[1]],
            holding: [this._holdingAnimations[0]]
        };

        this._walkAnimationDir = definitions.ACTOR_ANIMATION_DIR.NONE;
        this._walkAnimationMinSpeed = 200;
        this._walkAnimationMaxSpeed = 340;
        this._walkAnimationTime = 0;

        this._isIdleAnimation = false;
        this._isWalkAnimation = false;
        this._isAttackAnimation = false;
        this._isTakeDamageAnimation = false;
        this._isTalkAnimation = false;
        this._isQuestAnimation = false;
        this._isGrabAnimation = false;
        this._isEatAnimation = false;

        this._skinNormal = null;
        this._skinCombat = null;

        this._moveSpeedNormal = 2500;
        this._moveSpeedAttack = 900;

        this._stance = -1;
        this._stanceResetTime = 0;
        this._stanceResetDelay = 1.5;

        this.holdingOffset = {x: 50, y:-100};
        this._holdingObject = false;
    }

    init() {
        super.init();

        this.addViewWithSpine(spine("char_ivandoe"));

        this._view.stateData.defaultMix = 0.1;
        this._view.scale.set(0.245);

        this._skinNormal = new Skin("ivandoe_stance_normal");
        this._skinNormal.addSkin(this._view.spineData.findSkin("normal"));
        this._skinNormal.addSkin(this._view.spineData.findSkin("equip_sword"));
        this._skinNormal.addSkin(this._view.spineData.findSkin("outfit_standard"));

        this._skinCombat = new Skin("ivandoe_stance_combat");
        this._skinCombat.addSkin(this._view.spineData.findSkin("normal"));
        this._skinCombat.addSkin(this._view.spineData.findSkin("equip_sword"));
        this._skinCombat.addSkin(this._view.spineData.findSkin("outfit_standard"));

        this.stance = definitions.PLAYER_STANCE.NORMAL;

        this.addShadow(definitions.SHADOW_SIZE.MEDIUM, 1, new PIXI.Point(0, -4));

        this.playIdleAnimation();
    }

    destroy() {
        this._view.state.removeListener(this._spineListener);

        super.destroy();
    }

    update() {
        super.update();

        let animateRateScalar = 1;
        if (this._isWalkAnimation) {
            const v = this.velocity.length;
            animateRateScalar = ((v + this._walkAnimationMinSpeed) / (this._walkAnimationMaxSpeed + this._walkAnimationMinSpeed));
        }

        if (this.isMoving && !this.isAttacking && !this.isTakingDamage) {
            this.playWalkAnimation();
        }

        this._view.update(window.dt * animateRateScalar);
        this._walkAnimationTime = this._view.state.tracks[0].trackTime;

        this._shadow.position.set(this.x, this.y);

        this.resetCombatStance();
    }

    resetCombatStance() {
        if (this._stance === definitions.PLAYER_STANCE.COMBAT) {
            if (this._stanceResetTime > this._stanceResetDelay) {
                this.stance = definitions.PLAYER_STANCE.NORMAL;
            } else {
                this._stanceResetTime += window.dt;
            }
        }
    }

    attack() {
        if (this._stance === definitions.PLAYER_STANCE.DEAD)
            return;

        this.stance = definitions.PLAYER_STANCE.COMBAT;

        if (!this._isAttackAnimation) {
            this._stanceResetTime = 0;
            this._walkAnimationDir = definitions.ACTOR_ANIMATION_DIR.NONE;

            this.playAttackAnimation();
        }
    }

    /**
     * @param {number} amount
     */
    takeDamage(amount) {
        super.takeDamage(amount);

        this.stance = definitions.PLAYER_STANCE.COMBAT;
        this._stanceResetTime = 0;

        if (!this._isAttackAnimation) {
            this._walkAnimationDir = definitions.ACTOR_ANIMATION_DIR.NONE;
            this.playTakeDamageAnimation();
        }
    }

    die() {
        this.stance = definitions.PLAYER_STANCE.DEAD;

        this.playDeathAnimation();
    }

    reset() {
        this.stance = definitions.PLAYER_STANCE.NORMAL;

        this._stanceResetTime = 0;
        this._walkAnimationDir = definitions.ACTOR_ANIMATION_DIR.NONE;
    }

    playAnimation(name, loop, additive) {
        if (Array.isArray(name) ) {
            name = selectRandom(name);
        }

        if (typeof name == "string") {
            super.playAnimation(name, loop, additive);
            return;
        }

        let playBody = true;
        //console.log((this._isGrabAnimation || this._isEatAnimation), name);
        if((this._isGrabAnimation || this._isEatAnimation) &&
            (this._isInAnimationList(name, this._idleAnimations) ||
            this._isInAnimationList(name, this._walkAnimations)) )
        {
            playBody = false;
        }

        if (playBody) {
            if (this.holdingObject) {
                super.playAnimation(this._animationNames.holding[0].body, loop, additive, 0);
            }
            else if (name.body) {
                super.playAnimation(name.body, loop, additive, 0);
            }
        }   
        if (name.legs) {
            super.playAnimation(name.legs, loop, additive, 1);
        }
    }

    playTalkAnimation() {
        if (this._stance === definitions.PLAYER_STANCE.DEAD)
            return;

        this.playAnimation(this._talkAnimations, true);
    }

    playIdleAnimation() {
        const idleAnimations = [
            this._idleAnimations[0],
            this._idleAnimations[1],
            this._idleAnimations[2]
        ];

        if (this._stance === definitions.PLAYER_STANCE.DEAD || this._isQuestAnimation)
            return;

        this.moveSpeed = this._moveSpeedNormal;

        if (this.isTalking) {
          //  //console.log("talking");
            this.playAnimation(this._idleAnimations[3]);
        } else {
          //  //console.log("idling");
            this.playAnimation(idleAnimations);
        }
    }

    playWalkAnimation() {
        if (this._stance === definitions.PLAYER_STANCE.DEAD)
            return;

        this.moveSpeed = this._moveSpeedNormal;

        const dir = this.velocity.unit();
        const lastWalkingDir = this._walkAnimationDir;

        if (dir.dot(new P3.Vector2(0, -1)) > 0.5) {
            this._walkAnimationDir = definitions.ACTOR_ANIMATION_DIR.UP;
        } else if (dir.dot(new P3.Vector2(0, 1)) > 0.5) {
            this._walkAnimationDir = definitions.ACTOR_ANIMATION_DIR.DOWN;
        } else {
            if (dir.x < 0) {
                this._walkAnimationDir = definitions.ACTOR_ANIMATION_DIR.LEFT;
            } else if (dir.x >= 0) {
                this._walkAnimationDir = definitions.ACTOR_ANIMATION_DIR.RIGHT;
            }
        }

        if (this._walkAnimationDir !== lastWalkingDir) {
            let animationName;

            switch (this._stance) {
                case definitions.PLAYER_STANCE.NORMAL: {
                    switch (this._walkAnimationDir) {
                        case definitions.ACTOR_ANIMATION_DIR.UP: {
                            animationName = "walk_u";
                            break;
                        }
                        case definitions.ACTOR_ANIMATION_DIR.DOWN: {
                            animationName = "walk_d";
                            break;
                        }
                        case definitions.ACTOR_ANIMATION_DIR.LEFT: {
                            animationName = "walk_r";
                            break;
                        }
                        case definitions.ACTOR_ANIMATION_DIR.RIGHT: {
                            animationName = "walk_r";
                            break;
                        }
                    }
                    break;
                }
                case definitions.PLAYER_STANCE.COMBAT: {
                    switch (this._walkAnimationDir) {
                        case definitions.ACTOR_ANIMATION_DIR.UP: {
                            animationName = "walk_u_sword";
                            break;
                        }
                        case definitions.ACTOR_ANIMATION_DIR.DOWN: {
                            animationName = "walk_d_sword";
                            break;
                        }
                        case definitions.ACTOR_ANIMATION_DIR.LEFT: {
                            animationName = "walk_r_sword";
                            break;
                        }
                        case definitions.ACTOR_ANIMATION_DIR.RIGHT: {
                            animationName = "walk_r_sword";
                            break;
                        }
                    }
                    break;
                }
            }

            if (animationName) {
                this.playAnimation(this._animationNames[animationName], true);
                this._view.state.tracks[0].trackTime = this._walkAnimationTime;
            }
        }
    }

    playAttackAnimation() {
        if (this._stance === definitions.PLAYER_STANCE.DEAD)
            return;

        this.moveSpeed = this._moveSpeedAttack;

        this.playAnimation(this._attackAnimations); //["attack_r_sword_00", "attack_r_sword_01"]);
    }

    playEatAnimation() {
        if (this._stance === definitions.PLAYER_STANCE.DEAD)
            return;

        switch (this._stance) {
            case definitions.PLAYER_STANCE.NORMAL: {
                this.playAnimation(this._animationNames['eat']);
                break;
            }
            case definitions.PLAYER_STANCE.COMBAT: {
                this.playAnimation(this._animationNames['eat_sword']);
                break;
            }
        }
        this._isEatAnimation = true;
    }

    playGrabAnimation() {
        if (this._stance === definitions.PLAYER_STANCE.DEAD)
            return;

        switch (this._stance) {
            case definitions.PLAYER_STANCE.NORMAL: {
                this.playAnimation(this._animationNames['grab']);
                break;
            }
            case definitions.PLAYER_STANCE.COMBAT: {
                this.playAnimation(this._animationNames['grab_sword']);
                break;
            }
        }
        this._isGrabAnimation = true;
    }

    playTakeDamageAnimation() {
        if (this._stance === definitions.PLAYER_STANCE.DEAD)
            return;

        switch (this._stance) {
            case definitions.PLAYER_STANCE.NORMAL: {
                this.playAnimation(this._animationNames['hurt']);
                break;
            }
            case definitions.PLAYER_STANCE.COMBAT: {
                this.playAnimation(this._animationNames['hurt_sword']);
                break;
            }
        }

        P3.audio.playSound(['vo_iv_hurt_00', 'vo_iv_hurt_01', 'vo_iv_hurt_02', 'vo_iv_hurt_03', 'vo_iv_hurt_04', 'vo_iv_hurt_05', 'vo_iv_hurt_06', 'vo_iv_hurt_07']);
        P3.audio.playSound(["sfx_ivandoe_take_damage_00", "sfx_ivandoe_take_damage_01", "sfx_ivandoe_take_damage_02", "sfx_ivandoe_take_damage_03", "sfx_ivandoe_take_damage_04"]);
    }

    playQuestStartAnimation() {
        if (this._stance === definitions.PLAYER_STANCE.DEAD)
            return;

        this.playAnimation(this._animationNames['quest_start']);
    }

    playQuestFinishAnimation() {
        if (this._stance === definitions.PLAYER_STANCE.DEAD)
            return;

        this.playAnimation(this._animationNames['quest_complete']);
    }

    playDeathAnimation() {
        this.moveSpeed = 0;
        
        P3.audio.playSound(['vo_bert_gasp_00', 'vo_bert_err_00']);
        P3.audio.playSound(['vo_iv_faint_00', 'vo_iv_faint_01']);

        this.playAnimation(this._deathAnimations)//"die_r_00");
    }

    /**
     * @param {string} name
     * @param {Array} list
     */
    _isInAnimationList(name, list) {

        const arr = [];
        if (typeof name == 'string') {
            arr.push(name);
        }
        else {
            for (let i in name) {
                arr.push(name[i]);
            }
        } 
        for (let n in arr) {
            for (let i in list) {
                for (let j in list[i]) {
                    if (list[i][j] == arr[n]) {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    /**
     * @private
     */
    onMoveStop() {
        this._walkAnimationDir = definitions.ACTOR_ANIMATION_DIR.NONE;
        this._walkAnimationTime = 0;

        if (!this.isAttacking && !this.isTakingDamage) {
            this.playIdleAnimation();
        } 
    }

    /**
     * @param {string} name
     * @param entry
     */
    onAnimationStart(name, entry) {
        this._isIdleAnimation = this._isInAnimationList(name, this._idleAnimations);//this._idleAnimations.indexOf(name) >= 0;
        this._isWalkAnimation = this._isInAnimationList(name, this._walkAnimations);//this._walkAnimations.indexOf(name) >= 0;
        this._isAttackAnimation = this._isInAnimationList(name, this._attackAnimations);//this._attackAnimations.indexOf(name) >= 0;
        this._isTakeDamageAnimation = this._isInAnimationList(name, this._takeDamageAnimations);//this._takeDamageAnimations.indexOf(name) >= 0;
        this._isTalkAnimation = this._isInAnimationList(name, this._talkAnimations);//this._talkAnimations.indexOf(name) >= 0;
        this._isQuestAnimation = this._isInAnimationList(name, this._questAnimations);//this._questAnimations.indexOf(name) >= 0;
        //this._isGrabAnimation = this._isInAnimationList(name, this._grabAnimations);//this._grabAnimations.indexOf(name) >= 0;
        //this._isEatAnimation = this._isInAnimationList(name, this._eatAnimations);//this._grabAnimations.indexOf(name) >= 0;
    }
    
    /**
     * @param {string} name
     * @param entry
     */
    onAnimationComplete(name, entry) {

        this._isEatAnimation = false;
        this._isGrabAnimation = false;

        if (this._isInAnimationList(name, this._idleAnimations)) {
            this.playIdleAnimation();
        }
        if (this._isInAnimationList(name, this._eatAnimations) || 
            this._isInAnimationList(name, this._grabAnimations) ||
            this._isInAnimationList(name, this._attackAnimations) ||
            this._isInAnimationList(name, this._takeDamageAnimations)) {
            if (this.isMoving) {
                this._walkAnimationDir = definitions.ACTOR_ANIMATION_DIR.NONE;
                this.playWalkAnimation();
            } else {
                this.playIdleAnimation();
            }
        }
        if (this._isInAnimationList(name, this._talkAnimations)) {
            this.playTalkAnimation();
        }
        if (this._isInAnimationList(name, this._deathAnimations)) {
            P3.audio.playSound("vo_iv_asleep_loop_00");
        }
        if (this._isInAnimationList(name, this._questAnimations)) {
            this._isQuestAnimation = false;
            this.playIdleAnimation();
        }
    }

    onAnimationEvent(entry, event) {
        if (event.stringValue === "event_attack") {
            this.signals.attack.dispatch(this, 1);
            P3.audio.playSound(['vo_iv_effort_00', 'vo_iv_effort_01', 'vo_iv_effort_02', 'vo_iv_effort_03', 'vo_iv_effort_04', 'vo_iv_effort_05']);
            P3.audio.playSound(['sfx_action_sword_swipe_00', 'sfx_action_sword_swipe_01', 'sfx_action_sword_swipe_02', 'sfx_action_sword_swipe_03']);
        } else if(event.stringValue === "sfx_footstep") {
            P3.audio.playSound(['sfx_ivandoe_footstep_earth_00', 'sfx_ivandoe_footstep_earth_01', 'sfx_ivandoe_footstep_earth_02', 'sfx_ivandoe_footstep_earth_03']);
        } 
    }

    /**
     * @return {number}
     */
    get walkAnimationDir() {
        return this._walkAnimationDir;
    }

    /**
     * @return {number}
     */
    get stance() {
        return this._stance;
    }

    /**
     * @param {number} value
     */
    set stance(value) {
        if (this._stance !== value) {
            this._stance = value;
            this._walkAnimationDir = definitions.ACTOR_ANIMATION_DIR.NONE;

            switch (value) {
                case definitions.PLAYER_STANCE.DEAD:
                case definitions.PLAYER_STANCE.NORMAL: {
                    this.setSkin(this._skinNormal);
                    break;
                }
                case definitions.PLAYER_STANCE.COMBAT: {
                    this.setSkin(this._skinCombat);
                    break;
                }
            }
          //  //console.log("stance change");
        }
    }

    /**
     * @param {number} value
     */
    set holdingObject(value) {
        this._holdingObject = value;
        if (value) {
            this.playAnimation(this._holdingAnimations);
        }
        return this._holdingObject;
    }

    /**
     * @param {number} value
     */
    get holdingObject() {
        return this._holdingObject;
    }

    /**
     * @return {boolean}
     */
    get isAttacking() {
        return this._isAttackAnimation;
    }

    /**
     * @return {boolean}
     */
    get isTakingDamage() {
        return this._isTakeDamageAnimation;
    }

    /**
     * @return {boolean}
     */
    get isTalking() {
        return this._isTalkAnimation;
    }

    /**
     * @return {boolean}
     */
    get isQuest() {
        return this._isQuestAnimation;
    }

    /**
     * @return {boolean}
     */
    get isDead() {
        return this._stance === definitions.PLAYER_STANCE.DEAD;
    }
}
